import React, { useState, useEffect } from 'react';
import { payrollApi, Payroll, PayrollItem, SheetType } from '../services/payrollApi';
import { useAlert } from '../contexts/AlertContext';

const PayrollManagement: React.FC = () => {
  const { showToast, showConfirm } = useAlert();
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [selectedPayroll, setSelectedPayroll] = useState<Payroll | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [fetchingInsurance, setFetchingInsurance] = useState(false);

  // 이체파일 생성 모달 (4대보험 확인/입력)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [insuranceInput, setInsuranceInput] = useState('');
  const [generating, setGenerating] = useState(false);

  // 다운로드 모달 상태
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadYear, setDownloadYear] = useState(new Date().getFullYear());
  const [downloadMonth, setDownloadMonth] = useState(new Date().getMonth() + 1);

  // 수정 모달 상태
  const [editingItem, setEditingItem] = useState<PayrollItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    fetchPayrolls();
  }, []);

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const data = await payrollApi.getAll();
      setPayrolls(data);
    } catch (error) {
      showToast('급여대장 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFromHeum = async () => {
    try {
      setDownloading(true);
      const result = await payrollApi.downloadFromHeum(downloadYear, downloadMonth);
      showToast(result.message, 'success');
      setIsDownloadModalOpen(false);

      // 목록 새로고침 후 새로 다운받은 급여대장 자동 선택
      const data = await payrollApi.getAll();
      setPayrolls(data);

      // 방금 다운받은 급여대장 찾아서 선택
      const newPayroll = data.find(p => p.year === downloadYear && p.month === downloadMonth);
      if (newPayroll) {
        const detailed = await payrollApi.getById(newPayroll.id);
        setSelectedPayroll(detailed);
      }
    } catch (error: any) {
      showToast('혜움 다운로드 실패: ' + error.message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  // 이체파일 생성 버튼 → 4대보험 확인/입력 팝업 열기
  const handleGenerateTransferFile = (id: number) => {
    if (!selectedPayroll) return;
    setInsuranceInput(String(Math.round(Number(selectedPayroll.socialInsuranceAmount) || 0)));
    setIsTransferModalOpen(true);
  };

  // 팝업 내 "혜움 조회" — 4대보험 크롤해서 입력칸 채움 (선택, 느림)
  const handleFetchInsuranceInModal = async () => {
    if (!selectedPayroll) return;
    try {
      setFetchingInsurance(true);
      showToast('혜움에서 4대보험 조회 중... (최대 1분)', 'info');
      const result = await payrollApi.fetchInsurance(selectedPayroll.id);
      setInsuranceInput(String(Math.round(result.socialInsuranceAmount)));
      const updated = await payrollApi.getById(selectedPayroll.id);
      setSelectedPayroll(updated);
      showToast('4대보험 조회 완료', 'success');
    } catch (error: any) {
      showToast('4대보험 조회 실패: ' + error.message, 'error');
    } finally {
      setFetchingInsurance(false);
    }
  };

  // 팝업 확인 → 입력값 저장(변경 시) 후 이체파일 생성·다운로드
  const handleConfirmGenerate = async () => {
    if (!selectedPayroll) return;
    const amount = Math.round(Number(insuranceInput.replace(/,/g, '')) || 0);
    const id = selectedPayroll.id;
    try {
      setGenerating(true);
      // 입력값이 저장값과 다르면 먼저 저장
      if (amount !== Math.round(Number(selectedPayroll.socialInsuranceAmount) || 0)) {
        await payrollApi.setInsurance(id, amount);
      }

      const result = await payrollApi.generateHanaFile(id);
      if (result.success) {
        const downloadUrl = `${process.env.REACT_APP_API_URL || '/api'}/payroll/${id}/download-transfer-file`;
        const token = localStorage.getItem('accessToken');
        const response = await fetch(downloadUrl, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const today = new Date();
          const downloadDate = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
          a.download = `${selectedPayroll.year}_${String(selectedPayroll.month).padStart(2, '0')}_급여이체_${downloadDate}.xlsx`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      }
      setIsTransferModalOpen(false);
      fetchPayrolls();
      const updated = await payrollApi.getById(id);
      setSelectedPayroll(updated);
    } catch (error: any) {
      showToast('이체파일 생성 실패: ' + error.message, 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleEditItem = (item: PayrollItem) => {
    setEditingItem({ ...item });
    setIsEditModalOpen(true);
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;
    try {
      await payrollApi.updateItem(editingItem.id, editingItem);
      showToast('수정 완료', 'success');
      setIsEditModalOpen(false);
      if (selectedPayroll) {
        const updated = await payrollApi.getById(selectedPayroll.id);
        setSelectedPayroll(updated);
      }
    } catch (error: any) {
      showToast('수정 실패: ' + error.message, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await showConfirm('정말 삭제하시겠습니까?');
    if (!confirmed) return;
    try {
      await payrollApi.delete(id);
      showToast('삭제 완료', 'success');
      setSelectedPayroll(null);
      fetchPayrolls();
    } catch (error: any) {
      showToast('삭제 실패: ' + error.message, 'error');
    }
  };

  const handleSendEmail = async (id: number) => {
    const confirmed = await showConfirm('ceo@grkcon.com으로 이체파일을 전송하시겠습니까?');
    if (!confirmed) return;
    try {
      const result = await payrollApi.sendTransferEmail(id);
      showToast(result.message, 'success');
    } catch (error: any) {
      showToast('이메일 전송 실패: ' + error.message, 'error');
    }
  };

  // 4대보험 조회 (혜움 입출금 "사회보험" 출금액 크롤)
  const handleFetchInsurance = async (id: number) => {
    try {
      setFetchingInsurance(true);
      showToast('혜움에서 4대보험 조회 중... (최대 1분)', 'info');
      const result = await payrollApi.fetchInsurance(id);
      showToast(`4대보험 ${formatCurrency(result.socialInsuranceAmount)}원 조회 완료`, 'success');
      const updated = await payrollApi.getById(id);
      setSelectedPayroll(updated);
      fetchPayrolls();
    } catch (error: any) {
      showToast('4대보험 조회 실패: ' + error.message, 'error');
    } finally {
      setFetchingInsurance(false);
    }
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('ko-KR').format(Math.round(num));
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">급여대장 관리</h1>
        <button
          onClick={() => setIsDownloadModalOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          혜움에서 다운로드
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 급여대장 목록 */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">급여대장 목록</h2>
          <div className="space-y-2">
            {payrolls.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                급여대장이 없습니다.
              </div>
            ) : (
              payrolls.map((payroll) => (
                <div
                  key={payroll.id}
                  onClick={async () => {
                    const detailed = await payrollApi.getById(payroll.id);
                    setSelectedPayroll(detailed);
                  }}
                  className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                    selectedPayroll?.id === payroll.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">
                      {payroll.year}년 {payroll.month}월
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {payroll.employeeCount}명 / {formatCurrency(payroll.totalNetPayment)}원
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 급여대장 상세 */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-4">
          {selectedPayroll ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  {selectedPayroll.year}년 {selectedPayroll.month}월 급여대장
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleFetchInsurance(selectedPayroll.id)}
                    disabled={fetchingInsurance}
                    className="px-3 py-1 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
                  >
                    {fetchingInsurance ? '조회 중...' : '4대보험 조회'}
                  </button>
                  <button
                    onClick={() => handleGenerateTransferFile(selectedPayroll.id)}
                    className="px-3 py-1 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700"
                  >
                    이체파일 생성
                  </button>
                  <button
                    onClick={() => handleSendEmail(selectedPayroll.id)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                  >
                    메일전송
                  </button>
                  <button
                    onClick={() => handleDelete(selectedPayroll.id)}
                    className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                  >
                    삭제
                  </button>
                </div>
              </div>

              {/* 요약 정보 */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">대상 인원</div>
                  <div className="text-xl font-bold">{selectedPayroll.employeeCount}명</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">지급 총액</div>
                  <div className="text-xl font-bold text-blue-600">
                    {formatCurrency(selectedPayroll.totalPayment)}원
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">공제 총액</div>
                  <div className="text-xl font-bold text-red-600">
                    {formatCurrency(selectedPayroll.totalDeduction)}원
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-500">실지급 총액</div>
                  <div className="text-xl font-bold text-green-600">
                    {formatCurrency(selectedPayroll.totalNetPayment)}원
                  </div>
                </div>
              </div>

              {/* 4대보험 (급여이체 합산) */}
              <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span className="text-sm text-gray-600">4대 보험료 (급여이체 합산)</span>
                  <span className="ml-3 text-lg font-bold text-amber-700">
                    {formatCurrency(Number(selectedPayroll.socialInsuranceAmount) || 0)}원
                  </span>
                  {Number(selectedPayroll.socialInsuranceAmount) > 0 ? (
                    <span className="ml-2 text-xs text-gray-500">
                      {selectedPayroll.socialInsuranceMemo ? `· ${selectedPayroll.socialInsuranceMemo}` : ''}
                      {selectedPayroll.socialInsuranceFetchedAt
                        ? ` · ${new Date(selectedPayroll.socialInsuranceFetchedAt).toLocaleString('ko-KR')} 조회`
                        : ''}
                    </span>
                  ) : (
                    <span className="ml-2 text-xs text-gray-400">· 미조회 (매월 11일 자동 · 생성 팝업에서 조회/입력 가능)</span>
                  )}
                </div>
                <div className="text-sm text-gray-700">
                  총 이체금액:{' '}
                  <strong className="text-teal-700">
                    {formatCurrency(
                      Number(selectedPayroll.totalNetPayment) + (Number(selectedPayroll.socialInsuranceAmount) || 0),
                    )}
                    원
                  </strong>
                </div>
              </div>

              {/* 급여 항목 테이블 */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-2 py-2 text-left">성명</th>
                      <th className="px-2 py-2 text-right">기본급</th>
                      <th className="px-2 py-2 text-right">지급합계</th>
                      <th className="px-2 py-2 text-right">공제합계</th>
                      <th className="px-2 py-2 text-right">차인지급액</th>
                      <th className="px-2 py-2 text-center">구분</th>
                      <th className="px-2 py-2 text-center">수정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPayroll.items.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="px-2 py-2">{item.employeeName}</td>
                        <td className="px-2 py-2 text-right">{formatCurrency(item.baseSalary)}</td>
                        <td className="px-2 py-2 text-right text-blue-600">{formatCurrency(item.totalPayment)}</td>
                        <td className="px-2 py-2 text-right text-red-600">{formatCurrency(item.totalDeduction)}</td>
                        <td className="px-2 py-2 text-right font-semibold">{formatCurrency(item.netPayment)}</td>
                        <td className="px-2 py-2 text-center">
                          <span className={`px-2 py-1 text-xs rounded ${
                            item.sheetType === SheetType.WAGE_SALARY
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.sheetType === SheetType.WAGE_SALARY ? '근로' : '기타'}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <button
                            onClick={() => handleEditItem(item)}
                            className="text-blue-600 hover:underline"
                          >
                            수정
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-gray-500 text-center py-16">
              왼쪽에서 급여대장을 선택하세요.
            </div>
          )}
        </div>
      </div>

      {/* 다운로드 모달 */}
      {isDownloadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">혜움에서 급여대장 다운로드</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">년도</label>
                <select
                  value={downloadYear}
                  onChange={(e) => setDownloadYear(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {Array.from({ length: new Date().getFullYear() - 2024 + 1 }, (_, i) => 2024 + i).map((year) => (
                    <option key={year} value={year}>{year}년</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">월</label>
                <select
                  value={downloadMonth}
                  onChange={(e) => setDownloadMonth(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month}>{month}월</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsDownloadModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleDownloadFromHeum}
                disabled={downloading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {downloading ? '다운로드 중...' : '다운로드'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이체파일 생성 팝업 (4대보험 확인/입력) */}
      {isTransferModalOpen && selectedPayroll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[420px]">
            <h3 className="text-lg font-semibold mb-1">급여 이체파일 생성</h3>
            <p className="text-sm text-gray-500 mb-4">
              {selectedPayroll.year}년 {selectedPayroll.month}월 · 4대보험 금액 확인 후 생성하세요.
            </p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">급여 실지급 총액</span>
                <span className="font-semibold">{formatCurrency(selectedPayroll.totalNetPayment)}원</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">4대 보험료</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={insuranceInput ? Number(insuranceInput.replace(/,/g, '')).toLocaleString('ko-KR') : ''}
                    onChange={(e) => setInsuranceInput(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="0"
                    className="flex-1 px-3 py-2 border rounded-lg text-right"
                  />
                  <button
                    onClick={handleFetchInsuranceInModal}
                    disabled={fetchingInsurance}
                    className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {fetchingInsurance ? '조회 중...' : '혜움 조회'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  매월 11일 자동 조회됨. 고지서 값으로 직접 입력/수정 가능.
                </p>
              </div>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm font-medium text-gray-700">총 이체금액</span>
                <span className="text-lg font-bold text-teal-700">
                  {formatCurrency(Number(selectedPayroll.totalNetPayment) + (Number(insuranceInput.replace(/,/g, '')) || 0))}원
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsTransferModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleConfirmGenerate}
                disabled={generating || fetchingInsurance}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {generating ? '생성 중...' : '이체파일 생성'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {isEditModalOpen && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[600px] max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editingItem.employeeName} 급여 수정</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">기본급</label>
                <input
                  type="number"
                  value={editingItem.baseSalary}
                  onChange={(e) => setEditingItem({ ...editingItem, baseSalary: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">상여</label>
                <input
                  type="number"
                  value={editingItem.bonus}
                  onChange={(e) => setEditingItem({ ...editingItem, bonus: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">성과급</label>
                <input
                  type="number"
                  value={editingItem.performanceBonus}
                  onChange={(e) => setEditingItem({ ...editingItem, performanceBonus: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">자격수당</label>
                <input
                  type="number"
                  value={editingItem.qualificationAllowance}
                  onChange={(e) => setEditingItem({ ...editingItem, qualificationAllowance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">연차수당</label>
                <input
                  type="number"
                  value={editingItem.annualLeaveAllowance}
                  onChange={(e) => setEditingItem({ ...editingItem, annualLeaveAllowance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">식대</label>
                <input
                  type="number"
                  value={editingItem.mealAllowance}
                  onChange={(e) => setEditingItem({ ...editingItem, mealAllowance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">연구수당</label>
                <input
                  type="number"
                  value={editingItem.researchAllowance}
                  onChange={(e) => setEditingItem({ ...editingItem, researchAllowance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">육아수당</label>
                <input
                  type="number"
                  value={editingItem.childcareAllowance}
                  onChange={(e) => setEditingItem({ ...editingItem, childcareAllowance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">차량유지비</label>
                <input
                  type="number"
                  value={editingItem.vehicleAllowance}
                  onChange={(e) => setEditingItem({ ...editingItem, vehicleAllowance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">지급합계</label>
                <input
                  type="number"
                  value={editingItem.totalPayment}
                  onChange={(e) => setEditingItem({ ...editingItem, totalPayment: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-medium text-gray-700 mb-2">공제 항목</h4>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">국민연금</label>
                <input
                  type="number"
                  value={editingItem.nationalPension}
                  onChange={(e) => setEditingItem({ ...editingItem, nationalPension: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">건강보험</label>
                <input
                  type="number"
                  value={editingItem.healthInsurance}
                  onChange={(e) => setEditingItem({ ...editingItem, healthInsurance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">고용보험</label>
                <input
                  type="number"
                  value={editingItem.employmentInsurance}
                  onChange={(e) => setEditingItem({ ...editingItem, employmentInsurance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">장기요양보험</label>
                <input
                  type="number"
                  value={editingItem.longTermCare}
                  onChange={(e) => setEditingItem({ ...editingItem, longTermCare: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">소득세</label>
                <input
                  type="number"
                  value={editingItem.incomeTax}
                  onChange={(e) => setEditingItem({ ...editingItem, incomeTax: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">지방소득세</label>
                <input
                  type="number"
                  value={editingItem.localIncomeTax}
                  onChange={(e) => setEditingItem({ ...editingItem, localIncomeTax: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">공제합계</label>
                <input
                  type="number"
                  value={editingItem.totalDeduction}
                  onChange={(e) => setEditingItem({ ...editingItem, totalDeduction: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">차인지급액</label>
                <input
                  type="number"
                  value={editingItem.netPayment}
                  onChange={(e) => setEditingItem({ ...editingItem, netPayment: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleSaveItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollManagement;
